"use client";

import { useCallback, useEffect, useState } from "react";
import { useRouter } from "@tanstack/react-router";
import {
  IconArrowLeft,
  IconBriefcase,
  IconBuildingStore,
  IconLoader2,
  IconUserPlus,
  IconUsers,
} from "@tabler/icons-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AgencyClientsTable } from "@/components/agency-clients-table";
import { AgencyPeopleTable } from "@/components/agency-people-table";
import { InviteAgencyUsersDialog } from "@/features/agency/components";
import { useAgency } from "@/hooks/use-agencies";
import { isApiError } from "@/lib/axios";

interface AgencyDetailClientProps {
  agencyId: string;
}

const formatDate = (dateString: string): string =>
  new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

export default function AgencyDetailClient({
  agencyId,
}: AgencyDetailClientProps) {
  const router = useRouter();

  // Both tables own their own paging and search; the page keeps only the two
  // totals, so the summary cards can show them without a second read each.
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalClients, setTotalClients] = useState(0);

  // Bumped when the header's invite succeeds, so the people table returns to
  // its first page and the new invitees are visible from wherever the admin
  // was — the same thing the page did directly before the table moved out.
  const [peopleResetSignal, setPeopleResetSignal] = useState(0);

  // Read through a query hook rather than a local effect, so a mutation
  // anywhere can refresh it by invalidating the agencies key.
  const {
    data: agency,
    isLoading: isLoadingAgency,
    error: agencyError,
  } = useAgency(agencyId);

  // Set document title with truncated agency name
  useEffect(() => {
    if (!agency) return;
    const maxLen = 25;
    const titleName =
      agency.name.length > maxLen
        ? `${agency.name.slice(0, maxLen)}…`
        : agency.name;
    document.title = `${titleName} | Relivable | Venue Admin`;
    return () => {
      document.title = "Relivable | Venue Admin";
    };
  }, [agency]);

  // Return to the agencies list. Prefer browser history so the list restores
  // the exact page/search the user came from; fall back to a plain navigate
  // when there is no in-app history (e.g. opened directly).
  const handleBackToAgencies = useCallback(() => {
    if (window.history.length > 1) {
      router.history.back();
    } else {
      router.navigate({
        to: "/agencies",
        search: { page: 1, pageSize: 10, search: "" },
      });
    }
  }, [router]);

  if (isLoadingAgency) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <IconLoader2 className="size-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading agency details...</p>
        </div>
      </div>
    );
  }

  if (agencyError || !agency) {
    // An unknown id gets its own copy rather than a bare failure, because the
    // usual cause is a stale link rather than something being broken.
    const isNotFound =
      (isApiError(agencyError) && agencyError.statusCode === 404) || !agency;

    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="max-w-md text-center">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6">
            <h2 className="mb-2 text-lg font-semibold text-destructive">
              {isNotFound ? "Agency not found" : "Error"}
            </h2>
            <p className="text-sm text-destructive">
              {isNotFound
                ? "This agency does not exist, or it has been deleted."
                : agencyError instanceof Error
                  ? agencyError.message
                  : "Failed to load agency data"}
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={handleBackToAgencies}
            >
              Back to Agencies
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <div className="flex min-w-0 flex-1 flex-col gap-6 p-4 md:p-6">
        {/* Header */}
        <div className="flex min-w-0 items-center justify-between gap-4">
          <div className="flex min-w-0 flex-1 items-center gap-4 overflow-hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBackToAgencies}
              className="shrink-0"
            >
              <IconArrowLeft className="size-5" />
              <span className="sr-only">Back to agencies</span>
            </Button>
            <div className="min-w-0 flex-1">
              <h1
                className="min-w-0 max-w-md truncate text-3xl font-bold tracking-tight"
                title={agency.name}
              >
                {agency.name}
              </h1>
              <p className="text-muted-foreground mt-1">
                View and manage agency details, people and clients
              </p>
            </div>
          </div>
          <InviteAgencyUsersDialog
            agencyId={agencyId}
            agencyName={agency.name}
            onSuccess={() => setPeopleResetSignal((prev) => prev + 1)}
          >
            <Button className="gap-2">
              <IconUserPlus className="size-4" />
              Invite People
            </Button>
          </InviteAgencyUsersDialog>
        </div>

        {/* Agency Details Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {/* Agency Name Card */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Agency</CardTitle>
              <IconBriefcase className="size-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div
                className="min-w-0 max-w-md truncate text-2xl font-bold"
                title={agency.name}
              >
                {agency.name}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Created {formatDate(agency.createdAt)}
              </p>
            </CardContent>
          </Card>

          {/* People Count Card */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total People</CardTitle>
              <IconUsers className="size-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalUsers}</div>
              <p className="text-xs text-muted-foreground">
                {totalUsers === 1 ? "person" : "people"} in this agency
              </p>
            </CardContent>
          </Card>

          {/* Client Count Card */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Clients
              </CardTitle>
              <IconBuildingStore className="size-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalClients}</div>
              <p className="text-xs text-muted-foreground">
                {totalClients === 1 ? "venue" : "venues"} this agency works for
              </p>
            </CardContent>
          </Card>
        </div>

        {/* People Table */}
        <AgencyPeopleTable
          agencyId={agencyId}
          agencyName={agency.name}
          onTotalChange={setTotalUsers}
          resetPageSignal={peopleResetSignal}
        />

        {/* Clients Table */}
        <AgencyClientsTable
          agencyId={agencyId}
          agencyName={agency.name}
          onTotalChange={setTotalClients}
        />
      </div>
    </div>
  );
}
